import { useCallback, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { fetchApi } from '../../api';
import { statusForStage, type Deal, type DealItem, type StageItem } from './types';

interface DealModalProps {
  editingDeal: Deal | null;
  stages: StageItem[];
  conversations: any[];
  products: any[];
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}

interface DealFormState {
  title: string;
  conversationId: string;
  stage: string;
  discountPercent: number;
  manualAmount: number;
  items: DealItem[];
}

function initialForm(editingDeal: Deal | null, stages: StageItem[]): DealFormState {
  const fallbackStage = stages[0]?.id || 'Новая заявка';
  if (!editingDeal) {
    return { title: '', conversationId: '', stage: fallbackStage, discountPercent: 0, manualAmount: 0, items: [] };
  }
  return {
    title: editingDeal.title || '',
    conversationId: editingDeal.conversationId ? String(editingDeal.conversationId) : '',
    stage: editingDeal.stage || fallbackStage,
    discountPercent: editingDeal.discountPercent || 0,
    manualAmount: editingDeal.originalAmount || editingDeal.amount || 0,
    items: Array.isArray(editingDeal.items) ? [...editingDeal.items] : [],
  };
}

export default function DealModal({
  editingDeal,
  stages,
  conversations,
  products,
  onClose,
  onSaved,
  onError,
}: DealModalProps) {
  // The page mounts this with a key per deal, so the initial state is the whole
  // synchronisation story — no effect copying props into state.
  const [form, setForm] = useState<DealFormState>(() => initialForm(editingDeal, stages));
  const [saving, setSaving] = useState(false);

  const totals = useMemo(() => {
    const itemsSum = form.items.reduce((sum, item) => sum + (item.price || 0) * (item.qty || 1), 0);
    const original = form.items.length > 0 ? itemsSum : (Number(form.manualAmount) || 0);
    const discountPercent = Math.min(100, Math.max(0, Number(form.discountPercent) || 0));
    const discountAmount = Math.round(original * (discountPercent / 100) * 100) / 100;
    return { original, discountPercent, discountAmount, net: Math.max(0, original - discountAmount) };
  }, [form.items, form.manualAmount, form.discountPercent]);

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, title: event.target.value }));
  };

  const handleConversationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, conversationId: event.target.value }));
  };

  const handleStageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, stage: event.target.value }));
  };

  const handleManualAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, manualAmount: parseFloat(event.target.value) || 0 }));
  };

  const handleDiscountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(100, Math.max(0, parseFloat(event.target.value) || 0));
    setForm((prev) => ({ ...prev, discountPercent: value }));
  };

  const handleAddItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, { name: '', price: 0, qty: 1 }] }));
  };

  const handleRemoveItem = useCallback((index: number) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  }, []);

  const handleProductSelect = useCallback((index: number, productId: number) => {
    const product = products.find((candidate: any) => candidate.id === productId);
    if (!product) return;
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { productId: product.id, name: product.name, price: product.price || 0, qty: 1 };
      return { ...prev, items };
    });
  }, [products]);

  const handleItemFieldChange = useCallback((index: number, field: keyof DealItem, value: any) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      return { ...prev, items };
    });
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);

    const targetStage = stages.find((stage) => stage.id === form.stage);
    const payload = {
      title: form.title || 'Новая заявка',
      conversation_id: form.conversationId ? Number(form.conversationId) : null,
      stageId: targetStage?.stageId,
      stage: form.stage,
      // The status follows the stage's own type. It used to be derived from two
      // hardcoded stage names ("Выиграна"/"Проиграна") that no preset pipeline
      // actually contains, so closing a deal from this form never set `won`.
      status: statusForStage(targetStage),
      originalAmount: totals.original,
      discountPercent: totals.discountPercent,
      discountAmount: totals.discountAmount,
      amount: totals.net,
      items: form.items,
    };

    try {
      if (editingDeal) {
        await fetchApi(`/deals/${editingDeal.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await fetchApi('/deals', { method: 'POST', body: JSON.stringify(payload) });
      }
      onSaved();
    } catch (error: any) {
      // Carries the server's message verbatim — a refused write-off ("недостаточно
      // товара на складе") has to be readable, not swallowed into a console log.
      onError(error?.message || 'Ошибка при сохранении заявки');
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = () => onClose();
  const stopPropagation = (event: React.MouseEvent) => event.stopPropagation();

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="deal-modal" onClick={stopPropagation} role="dialog" aria-modal="true">
        <div className="deal-modal-header">
          <h2>{editingDeal ? `Редактирование заявки #${editingDeal.id}` : 'Создание новой заявки'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">×</button>
        </div>

        <form onSubmit={handleSubmit} className="deal-modal-body">
          <div className="form-group">
            <label htmlFor="deal-title">Название заявки *</label>
            <input
              id="deal-title"
              type="text"
              required
              placeholder="Например: Заявка на покупку видеокарты RTX 4070"
              value={form.title}
              onChange={handleTitleChange}
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label htmlFor="deal-conversation">Привязать к клиенту / чату</label>
              <select id="deal-conversation" value={form.conversationId} onChange={handleConversationChange}>
                <option value="">Не привязывать</option>
                {conversations.map((conversation: any) => (
                  <option key={conversation.id} value={conversation.id}>
                    {conversation.contactName} {conversation.contactPhone ? `(${conversation.contactPhone})` : ''} — #{conversation.id}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="deal-stage">Этап воронки</label>
              <select id="deal-stage" value={form.stage} onChange={handleStageChange}>
                {stages.map((stage) => (
                  <option key={stage.id} value={stage.id}>{stage.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-items-section">
            <div className="mis-header">
              <label>Товары / услуги в заявке</label>
              <button type="button" className="btn-add-item" onClick={handleAddItem}>
                <Plus size={14} /> Добавить позицию
              </button>
            </div>

            {form.items.length === 0 ? (
              <div className="mis-empty">Позиции пока не добавлены</div>
            ) : (
              form.items.map((item, index) => (
                <DealItemRow
                  key={index}
                  index={index}
                  item={item}
                  products={products}
                  onProductSelect={handleProductSelect}
                  onFieldChange={handleItemFieldChange}
                  onRemove={handleRemoveItem}
                />
              ))
            )}

            <div className="mis-total deal-totals">
              {form.items.length === 0 && (
                <div className="deal-totals-row">
                  <span className="deal-totals-label">Сумма заявки вручную (₽):</span>
                  <input
                    type="number"
                    min="0"
                    placeholder="0"
                    className="deal-totals-input"
                    value={form.manualAmount}
                    onChange={handleManualAmountChange}
                    aria-label="Сумма заявки вручную"
                  />
                </div>
              )}

              <div className="deal-totals-row">
                <span className="deal-totals-label">Скидка (%):</span>
                <div className="deal-totals-discount">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    className="deal-totals-input narrow"
                    value={form.discountPercent}
                    onChange={handleDiscountChange}
                    aria-label="Скидка в процентах"
                  />
                  <span className="deal-totals-label">%</span>
                </div>
              </div>

              <div className="deal-totals-summary">
                <div>
                  <span className="deal-totals-strong">Итоговая сумма:</span>
                  {totals.discountPercent > 0 && (
                    <span className="deal-totals-discount-note">
                      (скидка {totals.discountPercent}% = -{totals.discountAmount.toLocaleString('ru-RU')} ₽)
                    </span>
                  )}
                </div>
                <div className="deal-totals-values">
                  {totals.discountPercent > 0 && (
                    <span className="deal-totals-original">{totals.original.toLocaleString('ru-RU')} ₽</span>
                  )}
                  <b className="deal-totals-net">{totals.net.toLocaleString('ru-RU')} ₽</b>
                </div>
              </div>
            </div>
          </div>

          <div className="deal-modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Сохранение...' : editingDeal ? 'Сохранить изменения' : 'Создать заявку'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DealItemRowProps {
  index: number;
  item: DealItem;
  products: any[];
  onProductSelect: (index: number, productId: number) => void;
  onFieldChange: (index: number, field: keyof DealItem, value: any) => void;
  onRemove: (index: number) => void;
}

function DealItemRow({ index, item, products, onProductSelect, onFieldChange, onRemove }: DealItemRowProps) {
  const handleProductChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onProductSelect(index, Number(event.target.value));
  };
  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange(index, 'name', event.target.value);
  };
  const handleQtyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange(index, 'qty', parseInt(event.target.value, 10) || 1);
  };
  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange(index, 'price', parseFloat(event.target.value) || 0);
  };
  const handleRemove = () => onRemove(index);

  const selected = products.find((product: any) => product.id === item.productId);
  const availableStock = selected ? Number(selected.stock ?? 0) : null;
  const shortOfStock = availableStock !== null && selected?.productType === 'PHYSICAL' && item.qty > availableStock;

  return (
    <div className="item-row">
      <div className="ir-select">
        <select value={item.productId || ''} onChange={handleProductChange} aria-label="Товар из склада">
          <option value="">Выбрать из Склада...</option>
          {products.map((product: any) => (
            <option key={product.id} value={product.id}>
              {product.name} — {product.price?.toLocaleString('ru-RU')} ₽ (в наличии: {product.stock || 0})
            </option>
          ))}
        </select>
      </div>

      <div className="ir-name">
        <input type="text" placeholder="Название товара" value={item.name} onChange={handleNameChange} aria-label="Название позиции" />
      </div>

      <div className="ir-qty">
        <input type="number" min="1" placeholder="Кол-во" value={item.qty} onChange={handleQtyChange} aria-label="Количество" />
      </div>

      <div className="ir-price">
        <input type="number" placeholder="Цена ₽" value={item.price} onChange={handlePriceChange} aria-label="Цена" />
      </div>

      <button type="button" className="ir-remove" onClick={handleRemove} aria-label="Удалить позицию">×</button>

      {shortOfStock && (
        <div className="ir-stock-warning">
          На складе только {availableStock} шт — закрыть сделку с таким количеством не получится
        </div>
      )}
    </div>
  );
}
